import React, { useEffect, useMemo, useState } from "react";
import "./RomanTerms.css";

function clean(value, fallback = "General") {
  const text = String(value || "").trim();
  return text || fallback;
}

function termDescription(node) {
  return clean(
    node.description ||
      node.definition ||
      node.gloss ||
      node.meta?.gloss ||
      node.meta?.description ||
      node.meta?.definition ||
      node.meta?.meaning,
    "No definition has yet been added for this term."
  );
}

function buildIndex(data) {
  const nodes = Array.isArray(data?.nodes) ? data.nodes : [];
  const sections = new Map();

  for (const node of nodes) {
    if (node.type === "category") continue;

    const sectionName = clean(node.meta?.section, "Other Roman Terms");
    const categoryName = clean(node.meta?.category, "General");

    if (!sections.has(sectionName)) sections.set(sectionName, new Map());
    const categories = sections.get(sectionName);
    if (!categories.has(categoryName)) categories.set(categoryName, []);

    categories.get(categoryName).push({
      id: node.id || `${sectionName}-${categoryName}-${node.label}`,
      label: clean(node.label || node.term, "Untitled term"),
      description: termDescription(node),
      latin: clean(node.meta?.latin || node.latin, ""),
      source: node,
    });
  }

  return Array.from(sections.entries())
    .map(([title, categoryMap]) => ({
      title,
      categories: Array.from(categoryMap.entries())
        .map(([title, terms]) => ({
          title,
          terms: terms.sort((a, b) => a.label.localeCompare(b.label)),
        }))
        .sort((a, b) => a.title.localeCompare(b.title)),
    }))
    .sort((a, b) => a.title.localeCompare(b.title));
}

function searchableText(section, category, term) {
  return `${section.title} ${category.title} ${term.label} ${term.latin} ${term.description}`.toLowerCase();
}

export default function RomanTerms({ dataUrl = "/thesaurus.json" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [openSections, setOpenSections] = useState(() => new Set());
  const [openCategories, setOpenCategories] = useState(() => new Set());

  useEffect(() => {
    const controller = new AbortController();

    async function loadTerms() {
      try {
        setLoading(true);
        setError("");
        const response = await fetch(dataUrl, { signal: controller.signal });
        if (!response.ok) throw new Error(`The terms file returned ${response.status}.`);
        const json = await response.json();
        setData(json);
      } catch (loadError) {
        if (loadError.name !== "AbortError") {
          setError("The Roman terms could not be loaded. Check that thesaurus.json remains in the public folder.");
          console.error(loadError);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    loadTerms();
    return () => controller.abort();
  }, [dataUrl]);

  const index = useMemo(() => buildIndex(data), [data]);
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return index;

    return index
      .map((section) => ({
        ...section,
        categories: section.categories
          .map((category) => ({
            ...category,
            terms: category.terms.filter((term) => searchableText(section, category, term).includes(needle)),
          }))
          .filter((category) => category.terms.length > 0),
      }))
      .filter((section) => section.categories.length > 0);
  }, [index, query]);

  const totalTerms = useMemo(
    () => index.reduce((sum, section) => sum + section.categories.reduce((inner, category) => inner + category.terms.length, 0), 0),
    [index]
  );

  const visibleTerms = useMemo(
    () => filtered.reduce((sum, section) => sum + section.categories.reduce((inner, category) => inner + category.terms.length, 0), 0),
    [filtered]
  );

  function toggle(setter, key) {
    setter((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function expandAll() {
    setOpenSections(new Set(filtered.map((section) => section.title)));
    setOpenCategories(new Set(filtered.flatMap((section) => section.categories.map((category) => `${section.title}::${category.title}`))));
  }

  function collapseAll() {
    setOpenSections(new Set());
    setOpenCategories(new Set());
  }

  return (
    <main className="roman-terms-page">
      <header className="roman-terms-hero">
        <p className="roman-terms-eyebrow">Research Library</p>
        <h1>Ancient Roman Terms & Vocabulary</h1>
        <p>
          Browse military, political, religious, architectural and everyday terminology used across the Roman world.
        </p>

        <div className="roman-terms-toolbar">
          <label className="roman-terms-search">
            <span aria-hidden="true">⌕</span>
            <span className="roman-terms-sr-only">Search Roman terms</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search a term, definition or category…"
            />
            {query && <button type="button" onClick={() => setQuery("")} aria-label="Clear search">×</button>}
          </label>
          <div className="roman-terms-actions">
            <button type="button" onClick={expandAll}>Expand all</button>
            <button type="button" onClick={collapseAll}>Collapse all</button>
          </div>
        </div>

        {!loading && !error && (
          <p className="roman-terms-summary" aria-live="polite">
            {query ? `${visibleTerms} matching terms from ${totalTerms}` : `${totalTerms} terms`} across {index.length} main subjects.
          </p>
        )}
      </header>

      {loading && <div className="roman-terms-status">Loading Roman terms…</div>}
      {error && <div className="roman-terms-status roman-terms-error">{error}</div>}

      {!loading && !error && (
        <section className="roman-terms-list" aria-label="Roman terminology subjects">
          {filtered.map((section, sectionIndex) => {
            const sectionOpen = query ? true : openSections.has(section.title) || (sectionIndex === 0 && openSections.size === 0);
            const sectionId = `roman-section-${sectionIndex}`;
            const sectionCount = section.categories.reduce((sum, category) => sum + category.terms.length, 0);

            return (
              <article className="roman-terms-section" key={section.title}>
                <button
                  type="button"
                  className="roman-terms-section-button"
                  onClick={() => toggle(setOpenSections, section.title)}
                  aria-expanded={sectionOpen}
                  aria-controls={sectionId}
                >
                  <span>
                    <strong>{section.title}</strong>
                    <small>{section.categories.length} categories</small>
                  </span>
                  <span className="roman-terms-number">{sectionCount}</span>
                  <span className="roman-terms-chevron" aria-hidden="true">⌄</span>
                </button>

                {sectionOpen && (
                  <div className="roman-terms-section-panel" id={sectionId}>
                    {section.categories.map((category, categoryIndex) => {
                      const key = `${section.title}::${category.title}`;
                      const categoryOpen = query ? true : openCategories.has(key);
                      const categoryId = `roman-category-${sectionIndex}-${categoryIndex}`;

                      return (
                        <div className="roman-terms-category" key={key}>
                          <button
                            type="button"
                            className="roman-terms-category-button"
                            onClick={() => toggle(setOpenCategories, key)}
                            aria-expanded={categoryOpen}
                            aria-controls={categoryId}
                          >
                            <span>{category.title}</span>
                            <span>{category.terms.length}</span>
                          </button>

                          {categoryOpen && (
                            <div className="roman-terms-grid" id={categoryId}>
                              {category.terms.map((term) => (
                                <article className="roman-term-card" key={term.id}>
                                  <h2>{term.label}</h2>
                                  {term.latin && term.latin.toLowerCase() !== term.label.toLowerCase() && (
                                    <p className="roman-term-latin">{term.latin}</p>
                                  )}
                                  <p>{term.description}</p>
                                </article>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </article>
            );
          })}

          {filtered.length === 0 && (
            <div className="roman-terms-status">
              No terms matched “{query}”. <button type="button" onClick={() => setQuery("")}>Clear the search</button>
            </div>
          )}
        </section>
      )}
    </main>
  );
}
